import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { GlobalContext } from '../../../../library/Core/GlobalContext';
import SessionContext from '../../../../library/Core/SessionContext';
import { DomainElement } from '../../../../library/Core/SmartTypes';
import { ActivityDashboardService } from '../../alumni-activity-dashboard/activityDashboardService';
import { useVibrancy } from '../VibrancyDashboardContext';
import { InstituteType, Period, VibrancyDashboardService } from '../vibrancyDashboardService';

const TIMEFRAMES: { id: Period; label: string }[] = [
    { id: 'monthly',    label: 'Monthly'    },
    { id: 'quarterly',  label: 'Quarterly'  },
    { id: 'halfyearly', label: 'Half-Yearly'},
    { id: 'annual',     label: 'Annual'     },
];

const SESSION_GROUP_KEY = 'vd_selected_group';

/** Resolve K-12 / College type from INSTITUTE_DOMAIN flexCol (same as before). */
const resolveInstituteType = (el?: DomainElement): InstituteType => {
    if (!el?.flexCol) return 'k12';
    let flex: any = {};
    try { flex = JSON.parse(el.flexCol || '{}'); } catch { /* ignore */ }
    const raw = String(flex.institute_type ?? flex.type ?? flex.category ?? flex.category_code ?? '').toLowerCase();
    if (raw.includes('pg') || raw.includes('research')) return 'college_pg';
    if (raw.includes('college') || raw.includes('ug') || raw.includes('university')) return 'college_ug';
    return 'k12';
};

/** SM: Group dropdown + Institute typeahead (live API). Client: Campus dropdown. Both: period + compare. */
const SelectorBar: React.FC = () => {
    const {
        version, timeframe, compareMode, instituteLoaded,
        selectedGroupId, selectedInstituteId, setSelectedGroup, setSelectedInstitute, setTimeframe, setCompareMode,
    } = useVibrancy();

    const isSm = version === 'sm';
    const { globalState } = useContext(GlobalContext);
    const { sessionState } = useContext(SessionContext);

    // ── Client (institute admin login): institutes the user can access ──────────
    // Resolve sessionState.institutes (codes) against INSTITUTE_DOMAIN, published only.
    // A school-group admin resolves to more than one institute → show the dropdown.
    const clientInstitutes = useMemo(() => {
        if (isSm) return [];
        const codes: string[] = (sessionState?.institutes as any) ?? [];
        const dom = globalState.domain.get('INSTITUTE_DOMAIN') as DomainElement[] | undefined;
        return codes.flatMap((code) => {
            const el = dom?.find(d => String(d.code) === String(code));
            if (!el) return [];
            let published = true;
            try { published = JSON.parse(el.flexCol || '{}').is_published == 1; } catch { /* keep */ }
            return published ? [{ value: el.code, label: el.value, el }] : [];
        });
    }, [isSm, sessionState?.institutes, globalState.domain]);

    const isGroupSchool = clientInstitutes.length > 1;

    // Auto-select on load (client): default to the primary institute, else first.
    useEffect(() => {
        if (isSm || instituteLoaded) return;
        if (clientInstitutes.length > 0) {
            const primaryCode = sessionState?.primaryInstitute;
            const chosen = clientInstitutes.find(c => String(c.value) === String(primaryCode)) ?? clientInstitutes[0];
            setSelectedInstitute(chosen.value, chosen.label, resolveInstituteType(chosen.el));
        } else {
            VibrancyDashboardService.getClientInstitute()
                .then(r => { const inst: any = r.data?.data; if (inst) setSelectedInstitute(inst.id, inst.name, inst.type); })
                .catch(() => { /* leave unselected → empty state */ });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSm, instituteLoaded, clientInstitutes]);

    // ── Groups (API) ──────────────────────────────────────────────────────────
    const [groups, setGroups]               = useState<any[]>([]);
    const [selectedGroupValue, setGroupVal] = useState('');

    useEffect(() => {
        ActivityDashboardService.getGroups()
            .then(r => {
                const list = r.data?.data || [];
                setGroups(list);
                const saved = sessionStorage.getItem(SESSION_GROUP_KEY);
                if (saved) {
                    try {
                        const { id, name } = JSON.parse(saved);
                        const match = list.find((g: any) => String(g.id) === String(id));
                        if (match) {
                            setGroupVal(String(match.id));
                            setSelectedGroup(match.id, match.name || name);
                        }
                    } catch { /* ignore */ }
                }
            })
            .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Institute typeahead (API, 300ms debounce) ─────────────────────────────
    const [instSearch,      setInstSearch]      = useState('');
    const [suggestions,     setSuggestions]     = useState<any[]>([]);
    const [instLoading,     setInstLoading]     = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [instSelected,    setInstSelected]    = useState(false);
    const debounceRef  = useRef<any>(null);
    const wrapperRef   = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (instSelected) return;
        clearTimeout(debounceRef.current);
        if (instSearch.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
        debounceRef.current = setTimeout(async () => {
            setInstLoading(true);
            try {
                const r = await ActivityDashboardService.searchInstitutes(instSearch, selectedGroupId ?? undefined);
                const list = r.data?.data || [];
                setSuggestions(list);
                setShowSuggestions(true);
            } catch {
                setSuggestions([]);
                setShowSuggestions(true);
            } finally {
                setInstLoading(false);
            }
        }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instSearch, selectedGroupId]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node))
                setShowSuggestions(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleGroupChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setGroupVal(val);
        const opt = groups.find((g: any) => String(g.id) === val);
        const id   = opt ? opt.id   : null;
        const name = opt ? opt.name : '';
        if (id) sessionStorage.setItem(SESSION_GROUP_KEY, JSON.stringify({ id, name }));
        else    sessionStorage.removeItem(SESSION_GROUP_KEY);
        setSelectedGroup(id, name);
        setInstSearch(''); setInstSelected(false); setSuggestions([]);
    };

    const handleSelectInst = (inst: any) => {
        setInstSearch(inst.name); setInstSelected(true); setShowSuggestions(false); setSuggestions([]);
        // Resolve institute type from INSTITUTE_DOMAIN domain data (already in global state)
        const el = (globalState.domain.get('INSTITUTE_DOMAIN') as DomainElement[] | undefined)
            ?.find(d => String(d.code) === String(inst.id));
        setSelectedInstitute(inst.id, inst.name, resolveInstituteType(el));
    };

    const handleClearInst = () => {
        setInstSearch(''); setInstSelected(false); setSuggestions([]);
        setSelectedInstitute(null, '');
    };

    const isCompareDisabled = !instituteLoaded || timeframe === 'monthly';

    const onCompareClick = () => {
        if (!instituteLoaded) { toast.info('Select an institute first to use Compare.'); return; }
        if (timeframe === 'monthly') { toast.info('Switch to Quarterly, Half-Yearly, or Annual to use Compare.'); return; }
        setCompareMode(!compareMode);
    };

    return (
        <div className="bg-white border rounded shadow-sm p-3 mb-3">
            <div className="row g-3 align-items-end">

                {isSm ? (
                    <>
                        {/* Group dropdown — live API */}
                        <div className="col-md-4">
                            <label className="form-label small fw-semibold text-uppercase text-muted mb-1">
                                <i className="bi bi-buildings me-1"></i>Group
                            </label>
                            <select className="form-select form-select-sm" value={selectedGroupValue} onChange={handleGroupChange}>
                                <option value="">All Groups</option>
                                {groups.map((g: any) => (
                                    <option key={g.id} value={g.id}>{g.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Institute typeahead — live API */}
                        <div className="col-md-6" ref={wrapperRef} style={{ position: 'relative' }}>
                            <label className="form-label small fw-semibold text-uppercase text-muted mb-1">
                                <i className="bi bi-mortarboard me-1"></i>Institute
                            </label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white">
                                    {instLoading
                                        ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                                        : <i className="bi bi-search text-muted"></i>}
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search institute… (min 2 chars)"
                                    value={instSearch}
                                    autoComplete="off"
                                    onChange={e => { setInstSearch(e.target.value); setInstSelected(false); if (!e.target.value) setSelectedInstitute(null, ''); }}
                                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                                />
                                {instSearch && (
                                    <button className="btn btn-outline-secondary btn-sm" type="button" onClick={handleClearInst}>✕</button>
                                )}
                            </div>

                            {showSuggestions && (
                                <div className="border rounded bg-white shadow position-absolute w-100 mt-1"
                                    style={{ zIndex: 1050, maxHeight: 300, overflowY: 'auto', top: '100%' }}>
                                    {suggestions.length > 0 ? suggestions.map((inst: any) => (
                                        <div key={inst.id} className="px-3 py-2 border-bottom"
                                            style={{ cursor: 'pointer' }}
                                            onMouseDown={e => { e.preventDefault(); handleSelectInst(inst); }}>
                                            <div className="fw-semibold font-13">{inst.name}</div>
                                            {inst.group_name && <div className="text-muted" style={{ fontSize: 11 }}>{inst.group_name}</div>}
                                            {inst.city_name  && <div className="text-muted" style={{ fontSize: 11 }}><i className="bi bi-geo-alt me-1"></i>{inst.city_name}</div>}
                                        </div>
                                    )) : (
                                        <div className="px-3 py-2 text-muted small">
                                            No institutes found.{selectedGroupId ? ' Try a different name or clear the group filter.' : ' Try a different name.'}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    /* Client: institute dropdown — only for school-group admins (more than one institute) */
                    isGroupSchool && (
                        <div className="col-md-5">
                            <label className="form-label small fw-semibold text-uppercase text-muted mb-1">
                                <i className="bi bi-building me-1"></i>Institute
                            </label>
                            <select
                                className="form-select form-select-sm"
                                value={selectedInstituteId != null ? String(selectedInstituteId) : ''}
                                onChange={e => {
                                    const opt = clientInstitutes.find(c => String(c.value) === e.target.value);
                                    if (opt) setSelectedInstitute(opt.value, opt.label, resolveInstituteType(opt.el));
                                }}>
                                {clientInstitutes.map(c => (
                                    <option key={c.value} value={String(c.value)}>{c.label}</option>
                                ))}
                            </select>
                        </div>
                    )
                )}

                {/* Period chips + Compare toggle */}
                <div className="col-md-3 d-none">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      {/*  <label className="form-label small fw-semibold text-uppercase text-muted mb-0">
                            <i className="bi bi-calendar3 me-1"></i>Period
                        </label>
                         <button
                            className={`btn btn-sm ${compareMode ? 'btn-warning text-dark' : 'btn-outline-warning'} py-0 px-2`}
                            onClick={onCompareClick}
                            title={timeframe === 'monthly' ? 'Comparison not available in Monthly view.' : ''}
                            style={{ opacity: isCompareDisabled ? 0.5 : 1, cursor: isCompareDisabled ? 'not-allowed' : 'pointer' }}>
                            <i className="bi bi-lightning-charge-fill"></i>{' '}
                            {compareMode ? 'Comparing ON' : 'Compare'}
                        </button> */}
                    </div>
                    {/* <div className="btn-group w-100" role="group">
                        {TIMEFRAMES.map(tf => (
                            <button key={tf.id} type="button"
                                className={`btn btn-sm ${timeframe === tf.id ? 'btn-dark active' : 'btn-outline-secondary'}`}
                                disabled={!instituteLoaded}
                                style={{ opacity: !instituteLoaded ? 0.5 : 1, cursor: !instituteLoaded ? 'not-allowed' : 'pointer' }}
                                onClick={() => instituteLoaded && setTimeframe(tf.id)}>
                                {tf.label}
                            </button>
                        ))}
                    </div>
                    <select className="form-select form-select-sm mt-2" value={timeframe} onChange={e => setTimeframe(e.target.value as Period)}>
                        <option value="">Monthly</option>
                        <option value="">Quarterly</option>
                        <option value="">Half-Yearly</option>
                        <option value="">Annual</option>
                    </select> */}
                </div>

            </div>
        </div>
    );
};

export default SelectorBar;
